import { useCallback } from 'react';
import { useTranslation, LOCALE_IDS } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from '../../lib/brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for «صفحهٔ من» (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §12 — "all strings
// app-local fa keys first (kit-lag rule)").
//
// They belong in the kit's catalogues — `nav.items.myPage` in nav.json and the
// `myPage.*` tree in a new myPage.json namespace — and move there with the next
// kit release. Until then they live here, and `useMyPageStrings` asks the kit
// FIRST: the moment a released catalogue has a key, the kit's wording wins and
// this entry is dead, so the move is a deletion, never a behaviour change.
// Persian is authoritative; en, ar, fr and de carry every key (parity is tested).
// ─────────────────────────────────────────────────────────────────────────────

import fa from './strings/fa.json';
import en from './strings/en.json';
import ar from './strings/ar.json';
import fr from './strings/fr.json';
import de from './strings/de.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar), fr: flatten(fr), de: flatten(de) };

/**
 * Look a key up in the local table (English, then Persian, as the fallback) —
 * exported for tests and the shell. The brand's overlay and tokens apply as in the kit's
 * catalogues (src/lib/brandText.js); a list asked for with `vars` is read as
 * one string, as it always was.
 */
export function localMyPageString(locale, key, vars) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(vars ? String(value) : value, vars, from);
}

/** Whether this module owns a key (so a caller can fall back to it). */
export const hasLocalMyPageString = (key) => Object.prototype.hasOwnProperty.call(STRINGS.fa, key);

/**
 * `t` for this screen: the kit catalogue when it has the key, the local table
 * otherwise. Numbers are formatted by the caller (`formatNumber`) before they
 * are passed in, so both paths render the same digits.
 */
export function useMyPageStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) || !hasLocalMyPageString(key) ? t(key, vars) : localMyPageString(locale, key, vars)),
    [t, has, locale],
  );
}

// Every language the kit ships (a copy: a test may sort it), so a missing table
// here is a test failure rather than a quiet fallback.
export const MY_PAGE_STRING_LOCALES = [...LOCALE_IDS];
export const MY_PAGE_STRING_KEYS = Object.keys(STRINGS.fa);
export const myPageStringTable = (locale) => STRINGS[locale];
